<template>
  <div
    class="picker"
    data-test="clara-operation-picker"
  >
    <p class="picker__title">{{ $t('clara.operationPicker.title', { name: customer.name }) }}</p>

    <div
      v-if="loading"
      class="picker__hint"
    >
      {{ $t('clara.operationPicker.loading') }}
    </div>

    <div
      v-else-if="!operations.length"
      class="picker__hint"
      data-test="clara-operation-picker-empty"
    >
      {{ $t('clara.operationPicker.empty') }}
    </div>

    <ul
      v-else
      class="picker__list"
    >
      <li
        v-for="operation in operations"
        :key="operation.id"
      >
        <button
          type="button"
          class="picker__option"
          data-test="clara-operation-picker-option"
          @click="$emit('select', operation)"
        >
          <span class="picker__option-head">
            <span class="picker__option-id">#{{ operation.id }}</span>
            <span
              v-if="operation.status"
              class="picker__option-status"
            >{{ operation.status }}</span>
            <span
              v-if="operation.date"
              class="picker__option-date"
            >{{ operation.date }}</span>
          </span>

          <span
            class="picker__flow"
            data-test="clara-operation-picker-flow"
          >
            <span class="picker__leg">
              <img
                v-if="operation.origin.flag"
                :src="operation.origin.flag"
                :alt="operation.origin.code"
                class="picker__flag"
                data-test="clara-operation-picker-flag-origin"
              />
              <span class="picker__leg-code">{{ operation.origin.code }}</span>
              <span
                v-if="operation.origin.amount"
                class="picker__leg-amount"
              >{{ operation.origin.amount }}</span>
            </span>

            <PhArrowRight
              :size="11"
              class="picker__arrow"
            />

            <span class="picker__leg">
              <img
                v-if="operation.destination.flag"
                :src="operation.destination.flag"
                :alt="operation.destination.code"
                class="picker__flag"
                data-test="clara-operation-picker-flag-destination"
              />
              <span class="picker__leg-code">{{ operation.destination.code }}</span>
              <span
                v-if="operation.destination.amount"
                class="picker__leg-amount"
              >{{ operation.destination.amount }}</span>
            </span>
          </span>
        </button>
      </li>
    </ul>

    <button
      type="button"
      class="picker__cancel"
      data-test="clara-operation-picker-cancel"
      @click="$emit('cancel')"
    >
      {{ $t('clara.operationPicker.cancel') }}
    </button>
  </div>
</template>

<script>
// API
import OperationApi from '@/api/operation/operation-api';

// Models
import OperationFiltersModel from '@/model/operation-filters-model';

// Mixins
import mixinCurrencyFlag from '@/mixin/mixin-currency-flag';

// Utils
import utilsStorage from '@/utils/storage';
import { operationOriginCurrency } from '@/utils/operation-currency';

const MAX_RESULTS = 8;

export default {
  name: 'ClaraOperationPicker',

  mixins: [mixinCurrencyFlag],

  props: {
    customer: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      operations: [],
      loading: true
    };
  },

  created() {
    this.operationApi = new OperationApi(utilsStorage.getUserOnStorage());
    this.load();
  },

  methods: {
    async load() {
      try {
        const filtros = new OperationFiltersModel({
          customerId: this.customer.id,
          quantity: MAX_RESULTS,
          page: 1
        });

        const response = await this.operationApi.getOperations(filtros);

        this.operations = (response.data || []).slice(0, MAX_RESULTS).map(this.toOption);
      } catch {
        this.operations = [];
      } finally {
        this.loading = false;
      }
    },

    toOption(operation) {
      const foreign = this.toLeg(operation.currency?.code || '', operation.amount);
      const local = this.toLeg(operationOriginCurrency(operation), operation.totalValue);
      const [origin, destination] = operation.exchangeBuy
        ? [foreign, local]
        : [local, foreign];

      return {
        id: operation.id,
        status: operation.status?.description || operation.status?.name || null,
        date: operation.createdAt
          ? new Date(operation.createdAt).toLocaleDateString('pt-BR')
          : '',
        origin,
        destination,
        customer: { id: this.customer.id, name: this.customer.name }
      };
    },

    toLeg(code, value) {
      return {
        code,
        flag: this.getCurrencyFlag(code),
        amount: this.formatAmount(value)
      };
    },

    formatAmount(value) {
      const amount = Number(value);

      if (!value || Number.isNaN(amount)) return '';

      return amount.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
  }
};
</script>

<style scoped>
.picker {
  border: 1px solid var(--tc-line);
  border-radius: var(--tc-radius-md);
  background: var(--tc-surface);
  padding: var(--tc-space-3);
  margin-bottom: var(--tc-space-3);
}

.picker__title {
  margin: 0 0 var(--tc-space-2);
  font-size: 0.85rem;
  font-weight: var(--tc-fw-semibold);
  color: var(--tc-text);
}

.picker__hint {
  font-size: 0.8rem;
  color: var(--tc-text-2);
  padding: var(--tc-space-2) 0;
}

.picker__list {
  list-style: none;
  margin: 0 0 var(--tc-space-2);
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.picker__option {
  width: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: var(--tc-space-2);
  border-radius: var(--tc-radius-sm);
  background: transparent;
  transition: background var(--tc-transition);
}

.picker__option:hover {
  background: var(--clara-tint);
}

.picker__option-head {
  display: flex;
  align-items: baseline;
  gap: var(--tc-space-2);
}

.picker__option-id {
  font-family: var(--tc-font-mono);
  font-size: 0.8rem;
  font-weight: var(--tc-fw-semibold);
  color: var(--clara-text);
}

.picker__option-status {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: var(--tc-tracking-label);
  color: var(--tc-text-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.picker__option-date {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 0.7rem;
  color: var(--tc-text-3);
}

.picker__flow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--tc-space-1) var(--tc-space-2);
}

.picker__leg {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}

.picker__flag {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
}

.picker__leg-code {
  font-size: 0.7rem;
  font-weight: var(--tc-fw-semibold);
  letter-spacing: var(--tc-tracking-label);
  color: var(--tc-text-2);
}

.picker__leg-amount {
  font-size: 0.82rem;
  font-variant-numeric: tabular-nums;
  color: var(--tc-text);
}

.picker__arrow {
  flex-shrink: 0;
  color: var(--tc-text-2);
}

.picker__cancel {
  font-size: 0.78rem;
  color: var(--tc-text-2);
  padding: var(--tc-space-1) 0;
}

.picker__cancel:hover {
  color: var(--tc-text);
}
</style>
